import styled from 'styled-components'
import { useState, useCallback, useEffect } from 'react'
import { showLineDocuments } from '../documents/documentLineState'
import useTranslation from '../../hooks/useTranslation'
import GridListRow from './GridListRow'
import { editableColumnsOf } from './resolveColumn'
import { trailingCellsOf, TrailingCell } from './trailingCells'
import SortableGridListRow from './SortableGridListRow'
import { SkeletonTable } from '../generic/SkeletonLoader'
import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers'

const TableContent = styled.div`
    border: 1px solid ${({ theme }) => theme.table.border};
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
    background: ${({ theme }) => theme.table.background};

    @media (max-width: ${({ theme }) => theme.breakpoint.xs}) {
        display: none;
    }
`

const TableHeaderWrapper = styled.div`
    position: sticky;
    top: 0px;
    z-index: 99;
`

const HeaderRow = styled.header`
    background: ${({ theme }) => theme.table.header};
    display: flex;
    flex: 1 0 auto;
    min-width: 0px;
    min-height: 44px;
    position: relative;
    z-index: 100;
    border-bottom: 2px solid ${({ theme }) => theme.table.border};
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
    align-items: center;
    color: ${({ theme }) => theme.font.color};
    padding: 0px;
    padding-left: ${({ $reorderable }) => $reorderable ? '1.5rem' : '0'};
`

const HeaderCell = styled.div`
    box-sizing: border-box;
    flex: 0 1 ${props => props.$len || 'auto'};
    flex-shrink: 1;
    min-width: 0px;
    display: flex;
    /* No padding here: flex shares a shortfall out by each cell's width less
       its padding, so a padded header shrinks unlike the unpadded cells below
       and the columns drift apart. The label carries the padding instead. */
    padding: 0;
    align-items: center;
    justify-content: flex-start;
    overflow: hidden;
    overflow-wrap: break-word;
    word-break: break-word;
    border-right: 1px solid ${({ theme }) => theme.table.border};

    &:last-child {
        border-right: none;
    }

    p {
        font-size: var(--font-XS);
        line-height: 24px;
        margin: 0;
        padding: 4px 8px;
        box-sizing: border-box;
        display: block;
        color: ${({ theme }) => theme.font.color};
        font-weight: 700;
        user-select: auto;
        white-space: normal;
        max-width: 100%;
    }
`

const NoData = styled.div`
    display: flex;
    align-items: center;
    padding: 8px;
    color: ${({ theme }) => theme.font.color};
    font-size: var(--font-XS);
    border-top: 1px solid ${({ theme }) => theme.table.border};
    min-height: 36px;
    cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};

    &:hover {
        background-color: ${({ $clickable, theme }) => $clickable ? theme.table.hover : 'transparent'};
    }
`

const GridListTable = ({
    rows,
    columns,
    config,
    isLoading,
    isError,
    error,
    onAction,
    onUpdateCell,
    useStore,
    entity,
    reorderable,
    onReorder,
    onAppend,
    onAddFirst,
    dirtyRows = new Set(),
    savedRows = new Set(),
}) => {
    const { t } = useTranslation()
    const [cursor, setCursor] = useState(null)

    // Which cells can be edited depends on the row (a column's conditionals
    // can grey it out), so every move asks the row it lands on. A row that
    // does not exist yet — the one Tab is about to add — is an empty one.
    const editableOf = useCallback((rowIdx) => editableColumnsOf(columns, rows[rowIdx] || {}), [columns, rows])

    // Ctrl+↑/↓ keep the column; with Shift too they go to the row's first
    // field — Ctrl+Shift+↓ on the last row starts the next line from its start.
    // `browse` (plain arrows on a closed cell) moves without opening the cell
    // it lands on, and never adds a line: roaming, as in a spreadsheet.
    const navigate = useCallback((direction, currentRowIdx, currentColIdx, ctrlKey, shiftKey, browse = false) => {
        const here = editableOf(currentRowIdx)
        let nextRow = currentRowIdx
        let nextColIdx

        // The column in another row: the same one when it can be edited there,
        // else that row's first.
        const sameOrFirst = (rowIdx) => {
            const there = editableOf(rowIdx)
            return !shiftKey && there.includes(currentColIdx) ? currentColIdx : there[0]
        }

        switch (direction) {
            case 'up':
                if (!ctrlKey && !browse) return false
                nextRow = currentRowIdx - 1
                nextColIdx = sameOrFirst(nextRow)
                break
            case 'down':
                if (!ctrlKey && !browse) return false
                nextRow = currentRowIdx + 1
                nextColIdx = sameOrFirst(nextRow)
                break
            case 'right':
            case 'next': {
                const after = here.find(i => i > currentColIdx)
                if (after !== undefined) {
                    nextColIdx = after
                } else {
                    nextRow = currentRowIdx + 1
                    nextColIdx = editableOf(nextRow)[0]
                }
                break
            }
            case 'left':
            case 'prev': {
                const before = [...here].reverse().find(i => i < currentColIdx)
                if (before !== undefined) {
                    nextColIdx = before
                } else {
                    nextRow = currentRowIdx - 1
                    const there = editableOf(nextRow)
                    nextColIdx = there[there.length - 1]
                }
                break
            }
            default:
                return false
        }

        // Moving on past the last row starts a new one, when the grid asks
        // for it (tabAddsLine): entry carries on without reaching for the mouse.
        if (nextRow === rows.length && onAppend && !browse && (direction === 'next' || direction === 'down')) {
            onAppend()
            setCursor({ rowIdx: nextRow, colIdx: nextColIdx })
            return true
        }
        if (nextRow < 0 || nextRow >= rows.length || nextColIdx === undefined) return false
        setCursor({ rowIdx: nextRow, colIdx: nextColIdx, browse })
        return true
    }, [editableOf, rows.length, onAppend])

    // The row the cursor is on is the one the documents panel shows.
    useEffect(() => {
        if (config.lineDocuments && cursor) showLineDocuments(cursor.rowIdx)
    }, [config.lineDocuments, cursor?.rowIdx])

    const handleCellClick = useCallback((rowIdx, colIdx) => {
        setCursor({ rowIdx, colIdx })
    }, [])

    const handleCellEditingChange = useCallback((rowIdx, colIdx, isEditing) => {
        if (!isEditing) {
            setCursor({ rowIdx, colIdx })
        }
    }, [])

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
    )

    const handleDragEnd = (event) => {
        const { active, over } = event
        if (!over || active.id === over.id) return
        const oldIndex = rows.findIndex((_, i) => `grid-line-${i}` === active.id)
        const newIndex = rows.findIndex((_, i) => `grid-line-${i}` === over.id)
        if (oldIndex === -1 || newIndex === -1) return
        onReorder?.(oldIndex, newIndex)
    }

    if (isLoading) {
        return (
            <TableContent>
                <SkeletonTable />
            </TableContent>
        )
    }

    if (isError) {
        return (
            <TableContent>
                <NoData>Error: {error?.message || 'Failed to load data'}</NoData>
            </TableContent>
        )
    }

    const header = (
        <TableHeaderWrapper>
            <HeaderRow $reorderable={reorderable}>
                {columns.map(col => (
                    <HeaderCell key={col.id} $len={col.width}>
                        <p>{t(col.label ?? col.name)}</p>
                    </HeaderCell>
                ))}
                {trailingCellsOf(config).map((cell, i) => (
                    <TrailingCell key={cell.id} $len={cell.width} $first={i === 0} />
                ))}
            </HeaderRow>
        </TableHeaderWrapper>
    )

    // A grid that can add lines starts its first one from the empty row, with
    // the cursor in the first field, ready to type.
    if (rows.length === 0) {
        const addFirst = onAddFirst ? () => {
            onAddFirst()
            setCursor({ rowIdx: 0, colIdx: editableOf(0)[0] ?? 0 })
        } : undefined
        return (
            <TableContent>
                {header}
                {addFirst
                    ? <NoData $clickable role="button" tabIndex={0} onClick={addFirst}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addFirst() } }}>
                        {t('all.grid.empty.add')}
                    </NoData>
                    : <NoData>No data available.</NoData>}
            </TableContent>
        )
    }

    if (reorderable) {
        const sortableIds = rows.map((_, i) => `grid-line-${i}`)
        return (
            <TableContent>
                {header}
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                >
                    <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                        {rows.map((row, i) => (
                            <SortableGridListRow
                                key={row.id || row._key || i}
                                row={row}
                                rowIdx={i}
                                columns={columns}
                                config={config}
                                onAction={onAction}
                                onUpdateCell={onUpdateCell}
                                useStore={useStore}
                                entity={entity}
                                sortableId={`grid-line-${i}`}
                                cursor={cursor}
                                onNavigate={navigate}
                                onCellClick={handleCellClick}
                                onCellEditingChange={handleCellEditingChange}
                                isDirty={dirtyRows.has(i)}
                                isSaved={savedRows.has(i)}
                                reorderable={reorderable}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </TableContent>
        )
    }

    return (
        <TableContent>
            {header}
            {rows.map((row, i) => (
                <GridListRow
                    key={row.id || row._key || i}
                    row={row}
                    rowIdx={i}
                    columns={columns}
                    config={config}
                    onAction={onAction}
                    onUpdateCell={onUpdateCell}
                    useStore={useStore}
                    entity={entity}
                    cursor={cursor}
                    onNavigate={navigate}
                    onCellClick={handleCellClick}
                    onCellEditingChange={handleCellEditingChange}
                    isDirty={dirtyRows.has(i)}
                    isSaved={savedRows.has(i)}
                />
            ))}
        </TableContent>
    )
}

export default GridListTable
